import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CheckCircle2, XCircle, Loader2, X } from 'lucide-react';
import { useSubscription } from '../../hooks/useSubscription';

/**
 * Handles the return from Stripe Checkout via the ?checkout= query param on /app:
 *   - success: poll the subscription a few times (the webhook may lag a second or
 *     two), show a confirmation toast, and clear the query param.
 *   - cancel: show a gentle "checkout canceled" note and clear the param.
 *
 * Renders a small auto-dismissing toast. Never blocks the app.
 */
type NoticeKind = 'success' | 'cancel';

export function CheckoutReturnNotice() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { isSubscribed, refetch } = useSubscription();
  const [notice, setNotice] = useState<NoticeKind | null>(null);
  const handled = useRef(false);

  // Detect the return param once, clear it, and (on success) poll for activation.
  useEffect(() => {
    const checkout = searchParams.get('checkout');
    if (handled.current || (checkout !== 'success' && checkout !== 'cancel')) return;
    handled.current = true;
    setNotice(checkout);

    // Remove ?checkout so a refresh doesn't replay the notice.
    const next = new URLSearchParams(searchParams);
    next.delete('checkout');
    setSearchParams(next, { replace: true });

    if (checkout !== 'success') return;

    // Webhook lag: refetch a few times until Pro shows up (or we give up).
    let active = true;
    let attempts = 0;
    const poll = async () => {
      if (!active) return;
      attempts += 1;
      await refetch();
      if (active && attempts < 6) window.setTimeout(poll, 1500);
    };
    void poll();
    return () => {
      active = false;
    };
  }, [searchParams, setSearchParams, refetch]);

  // Auto-dismiss the toast after a few seconds.
  useEffect(() => {
    if (!notice) return;
    const t = window.setTimeout(() => setNotice(null), notice === 'success' ? 7000 : 5000);
    return () => window.clearTimeout(t);
  }, [notice]);

  if (!notice) return null;

  const success = notice === 'success';
  // Any tier: this asks whether the webhook has landed yet, not what was bought.
  // Testing Pro specifically would spin forever after a Plus purchase.
  const activating = success && !isSubscribed;

  const message = success
    ? activating
      ? 'Payment received. Activating your Pro access…'
      : "You're on Pro 🎉 Thanks for upgrading!"
    : 'Checkout cancelled. No charge was made.';

  const accent = success ? 'rgba(109,185,129,0.45)' : 'rgba(212,180,131,0.4)';
  const bg = success ? 'rgba(16,32,24,0.96)' : 'rgba(34,28,20,0.96)';

  return (
    <div
      className="fixed left-1/2 z-50 -translate-x-1/2"
      style={{ top: 84 }}
      role="status"
      aria-live="polite"
    >
      <div
        className="flex items-center gap-3 rounded-xl px-4 py-3 shadow-xl"
        style={{
          backgroundColor: bg,
          border: `0.5px solid ${accent}`,
          backdropFilter: 'blur(8px)',
          maxWidth: 'min(92vw, 460px)',
        }}
      >
        {success ? (
          activating ? (
            <Loader2 className="w-4 h-4 flex-shrink-0 animate-spin" style={{ color: '#6db981' }} />
          ) : (
            <CheckCircle2 className="w-4 h-4 flex-shrink-0" style={{ color: '#6db981' }} />
          )
        ) : (
          <XCircle className="w-4 h-4 flex-shrink-0" style={{ color: '#d4b483' }} />
        )}

        <span className="text-[13px]" style={{ color: '#f4f1ea' }}>
          {message}
        </span>

        <button
          onClick={() => setNotice(null)}
          className="flex-shrink-0 opacity-60 transition-opacity hover:opacity-100"
          style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#f4f1ea' }}
          aria-label="Dismiss"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}
